import { useState, useEffect } from "react";
import { useAuth } from "@/lib/auth";
import { useBranding } from "@/lib/branding";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  House, ChartBar, GearSix, SignOut, Scales, List, X, Briefcase, Sun, Moon, Desktop, FileText, Megaphone, Handshake,
} from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import { api } from "@/lib/api";
import { toast } from "sonner";
import InstallAppButton from "@/components/InstallAppButton";

const NAV = [
  { key: "dashboard", to: "/", label: "Dashboard", icon: House, roles: ["admin", "manager", "user", "guest"] },
  { key: "leads", to: "/leads", label: "Leads", icon: Briefcase, roles: ["admin", "manager", "user", "guest"] },
  { key: "sales", to: "/sales", label: "Sales", icon: Handshake, roles: ["admin", "manager", "user"], salesGate: true },
  { key: "marketing", to: "/marketing", label: "Marketing", icon: Megaphone, roles: ["admin", "manager", "user", "guest"] },
  { key: "analytics", to: "/analytics", label: "Analytics", icon: ChartBar, roles: ["admin", "manager", "guest"] },
  { key: "reports", to: "/reports", label: "Reports", icon: FileText, roles: ["admin", "guest"] },
  { key: "settings", to: "/settings", label: "Settings", icon: GearSix, roles: ["admin"] },
];
// Fallback for guests whose admin never picked which pages they can see —
// keeps the previous default so upgrading tenants aren't surprised.
const DEFAULT_GUEST_NAV = ["dashboard", "leads", "marketing"];

export default function Layout({ children }) {
  const { user, photo, logout } = useAuth();
  const { branding, theme, setTheme } = useBranding();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);

  const isAdmin = user?.role === "admin";

  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get("/users");
        if (!cancelled) setPendingCount(data.filter((u) => u.status === "pending").length);
      } catch (e) {
        // non-admins or auth issues will 403 here; log for debugging only.
        console.debug("pending-users count fetch failed:", e?.response?.status || e?.message);
      }
    };
    load();
    const t = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(t); };
  }, [isAdmin]);

  const visibleNav = (() => {
    const roleFiltered = NAV.filter((item) => item.roles.includes(user?.role || "user"));
    // Sales tab: only admins or users explicitly flagged "Incl Sales".
    const salesGated = roleFiltered.filter((item) =>
      !item.salesGate || user?.role === "admin" || user?.incl_sales);
    if (user?.role !== "guest") return salesGated;
    const allowed = (user?.guest_allowed_nav && user.guest_allowed_nav.length > 0)
      ? user.guest_allowed_nav
      : DEFAULT_GUEST_NAV;
    return salesGated.filter((item) => allowed.includes(item.key));
  })();

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const isActive = (to) => (to === "/" ? pathname === "/" : pathname.startsWith(to));

  const initials = (user?.name || user?.email || "?")
    .split(/[ .@]/).filter(Boolean).slice(0, 2).map((p) => p[0]?.toUpperCase()).join("");

  const ThemeIconBtn = ({ mode, icon: Icon, label }) => (
    <button
      type="button"
      data-testid={`theme-${mode}-btn`}
      onClick={() => setTheme(mode)}
      title={label}
      className={
        "flex-1 h-9 flex items-center justify-center rounded-sm transition-colors " +
        (theme === mode ? "" : "hover:bg-black/5 dark:hover:bg-white/10")
      }
      style={
        theme === mode
          ? { backgroundColor: "var(--brand-primary)", color: "#FFFFFF" }
          : { color: "var(--brand-sidebar-text)", opacity: 0.6 }
      }
    >
      <Icon size={16} weight={theme === mode ? "fill" : "regular"} />
    </button>
  );

  return (
    <div className="min-h-screen bg-background flex">
      {/* Sidebar - desktop */}
      <aside
        data-testid="sidebar"
        className={cn(
          "fixed lg:sticky top-0 left-0 z-40 h-screen w-64 shrink-0 border-r border-border transition-transform duration-300 lg:translate-x-0 brand-sidebar",
          sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        )}
      >
        <div className="flex flex-col h-full">
          <div className="h-16 flex items-center gap-3 px-6" style={{ borderBottom: "1px solid color-mix(in srgb, var(--brand-sidebar-text) 12%, transparent)" }}>
            {branding.logo_url ? (
              <img
                src={branding.logo_url}
                alt={branding.firm_name}
                className="h-9 w-9 rounded-sm object-cover bg-[var(--brand-primary)]"
              />
            ) : (
              <div
                className="h-9 w-9 rounded-sm flex items-center justify-center"
                style={{ backgroundColor: "var(--brand-primary)" }}
              >
                <Scales className="text-white" size={20} weight="bold" />
              </div>
            )}
            <div className="leading-tight min-w-0">
              <div className="font-display text-lg font-medium truncate" style={{ color: "var(--brand-sidebar-text)" }}>{branding.firm_name}</div>
              <div className="label-tracking text-[0.65rem] !tracking-[0.18em]" style={{ color: "var(--brand-sidebar-text)", opacity: 0.6 }}>{branding.tagline}</div>
            </div>
            <button
              data-testid="sidebar-close-btn"
              onClick={() => setSidebarOpen(false)}
              className="ml-auto lg:hidden opacity-70 hover:opacity-100"
              style={{ color: "var(--brand-sidebar-text)" }}
            >
              <X size={18} />
            </button>
          </div>

          <nav className="flex-1 px-3 py-6 space-y-1">
            {visibleNav.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.to);
              const showBadge = item.to === "/settings" && pendingCount > 0;
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={() => setSidebarOpen(false)}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                  className={cn(
                    "flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm transition-colors",
                    active ? "text-white" : "hover:opacity-100"
                  )}
                  style={
                    active
                      ? { backgroundColor: "var(--brand-primary)" }
                      : { color: "var(--brand-sidebar-text)", opacity: 0.75 }
                  }
                >
                  <Icon size={18} weight={active ? "fill" : "regular"} />
                  <span>{item.label}</span>
                  {showBadge && (
                    <span
                      data-testid="nav-pending-badge"
                      className="ml-auto inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-amber-500 text-white text-[0.6rem] font-semibold"
                      title={`${pendingCount} user(s) awaiting approval`}
                    >
                      {pendingCount}
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>

          <div className="p-4 space-y-3" style={{ borderTop: "1px solid color-mix(in srgb, var(--brand-sidebar-text) 12%, transparent)" }}>
            {/* Per-user theme preference */}
            <div className="flex gap-1" data-testid="theme-switcher">
              <ThemeIconBtn mode="light" icon={Sun} label="Light theme" />
              <ThemeIconBtn mode="dark" icon={Moon} label="Dark theme" />
              <ThemeIconBtn mode="system" icon={Desktop} label="Follow system" />
            </div>
            <div className="flex items-center gap-3 min-w-0">
              {photo ? (
                <img
                  src={photo}
                  alt={user?.name}
                  data-testid="user-photo"
                  className="h-9 w-9 rounded-full object-cover"
                  style={{ boxShadow: "0 0 0 2px var(--brand-sidebar-bg), 0 0 0 3px rgba(0,0,0,0.08)" }}
                />
              ) : (
                <div
                  data-testid="user-initials"
                  className="h-9 w-9 rounded-full flex items-center justify-center text-white text-xs font-medium shrink-0"
                  style={{ backgroundColor: "var(--brand-accent)" }}
                >
                  {initials}
                </div>
              )}
              <div className="text-xs min-w-0 flex-1">
                <div className="font-medium truncate" style={{ color: "var(--brand-sidebar-text)" }}>{user?.name}</div>
                <div className="truncate" style={{ color: "var(--brand-sidebar-text)", opacity: 0.6 }}>{user?.email}</div>
              </div>
            </div>
            <button
              data-testid="logout-btn"
              onClick={handleLogout}
              className="w-full flex items-center gap-2 justify-start text-xs px-3 py-2 rounded-sm transition-colors hover:bg-black/5 dark:hover:bg-white/10"
              style={{ color: "var(--brand-sidebar-text)", border: "1px solid var(--brand-sidebar-text)", borderColor: "color-mix(in srgb, var(--brand-sidebar-text) 25%, transparent)" }}
            >
              <SignOut size={14} />
              Sign out
            </button>
            <InstallAppButton />
          </div>
        </div>
      </aside>

      {/* Mobile backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-foreground/30 backdrop-blur-sm lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="sticky top-0 z-20 brand-header glass-nav h-16 flex items-center px-4 lg:px-8">
          <button
            data-testid="sidebar-toggle-btn"
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden mr-3 text-foreground"
          >
            <List size={22} />
          </button>
          <div className="label-tracking">{pathname === "/" ? "Dashboard" : pathname.split("/")[1]}</div>
          <div className="ml-auto flex items-center gap-3 text-xs text-muted-foreground">
            <span className="hidden sm:inline">{user?.email}</span>
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-10">
          {children}
        </main>
      </div>
    </div>
  );
}
